import { Box, Center, Checkbox, HStack, Input, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr, VStack } from "@chakra-ui/react"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useInViewport } from "react-in-viewport"
import { UserGroupsByKeywordQueryVariables, useUserGroupsByKeywordQuery } from "../../graphql"

export type UserGroupSelectorProps = {
	onUpdate: (userIds: string[]) => void
	value: string[]
}

export const UserGroupSelector: React.FC<UserGroupSelectorProps> = ({ value, onUpdate }) => {
	const [variables, setVariables] = useState<UserGroupsByKeywordQueryVariables>({ keyword: "", pagination: { limit: 20, page: 1 } })

	const [{ data, fetching, error }] = useUserGroupsByKeywordQuery({ variables })

	const nextPage = useCallback(() => {
		if (fetching) return

		if (data?.userGroupsByKeyword.hasNextPage) {
			setVariables((prev) => ({
				...prev,
				pagination: {
					...prev.pagination,
					page: (prev.pagination.page || 0) + 1,
				},
			}))
		}
	}, [data, fetching])

	const ref = useRef()

	const { inViewport } = useInViewport(ref as any, { threshold: 0.25 })

	useEffect(() => {
		if (inViewport) {
			nextPage()
		}
	}, [inViewport])

	const [keyword, setKeyword] = useState("")

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setVariables((prev) => ({
				...prev,
				keyword,
				pagination: { ...prev.pagination, page: 1 },
			}))
		}, 400)

		return () => {
			clearTimeout(timeoutId)
		}
	}, [keyword])

	const handleChange = (UserId: string, isChecked: boolean) => {
		if (isChecked) {
			onUpdate([...value, UserId])
		} else {
			const _value = [...value]
			_value.splice(_value.indexOf(UserId), 1)
			onUpdate(_value)
		}
	}

	const isuserGroupselected = useCallback((UserId: string) => value.includes(UserId), [value])

	const isAllSelected = useMemo(() => data?.userGroupsByKeyword.userGroups.map((u) => u._id).every((uid) => value.includes(uid)), [data, value])
	const isNoneSelected = useMemo(() => !value.length, [data, value])

	return (
		<VStack w="full" align="stretch">
			<HStack w="full" flexWrap="wrap" justify="space-between">
				<Input flex="1" variant="filled" bgColor="grayscale.input-background" placeholder="Search" _placeholder={{ color: "grayscale.placeholer" }} value={keyword} onChange={(e) => setKeyword(e.target.value)} />
			</HStack>
			{fetching && !data?.userGroupsByKeyword.userGroups.length ? (
				<Center w="full" py="4">
					<VStack w="full" color="grayscale.label">
						<Text fontSize="sm">Loading user groups</Text>
						<Spinner size="sm" />
					</VStack>
				</Center>
			) : error ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						{error.message.replace("[GraphQL] ", "")}
					</Text>
				</Center>
			) : !data?.userGroupsByKeyword.userGroups.length ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						Couldn&apos;t find any user groups.
					</Text>
				</Center>
			) : (
				<VStack w="full" align="stretch" h="full" maxH={{ base: "sm", xl: "lg" }} overflowY="auto" spacing={0}>
					<Table>
						<Thead pos="sticky" top="0" w="full" zIndex={10} bgColor="white">
							<Tr>
								<Th>
									<Checkbox
										isDisabled={data.userGroupsByKeyword.hasNextPage}
										isChecked={isAllSelected}
										isIndeterminate={!isAllSelected && !isNoneSelected}
										onChange={(e) => {
											e.target.checked ? onUpdate(data.userGroupsByKeyword.userGroups.map((r) => r._id)) : onUpdate([])
										}}
									>
										<Text fontSize="xs" textTransform="lowercase">
											{value.length} selected
										</Text>
									</Checkbox>
								</Th>
								<Th>User</Th>
							</Tr>
						</Thead>
						<Tbody>
							{data?.userGroupsByKeyword.userGroups.map((userGroup) => (
								<Tr key={userGroup._id}>
									<Td>
										<Checkbox isChecked={isuserGroupselected(userGroup._id)} onChange={(e) => handleChange(userGroup._id, e.target.checked)} />
									</Td>
									<Td>{userGroup.label.name}</Td>
								</Tr>
							))}
							<Box w="full" h="2" ref={ref as any} />
						</Tbody>
					</Table>
					{fetching && (
						<Center w="full" py="4">
							<VStack w="full" color="grayscale.label">
								<Text fontSize="sm">Loading more user groups</Text>
								<Spinner size="sm" />
							</VStack>
						</Center>
					)}
				</VStack>
			)}
		</VStack>
	)
}
